import axios from 'axios';
import { authService } from '@/features/auth/model/auth-service';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';

// On localhost, use relative '/api' so the Vite dev server proxy forwards requests to the backend.
// In all deployed environments (dev, stage, prod), use the absolute API_URL baked in at build time
// since Azure Static Web Apps has no reverse proxy for /api routes.
const baseURL = import.meta.env.PROD ? `${import.meta.env.API_URL}/api` : '/api';

export const apiClient = axios.create({
	baseURL,
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Request interceptor: Inject auth token
apiClient.interceptors.request.use(
	async (config: InternalAxiosRequestConfig) => {
		const token = await authService.getAccessToken();
		if (token && config.headers) {
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error)
);

// Single-flight refresh: many concurrent 401s should trigger exactly one refresh call.
let pendingRefresh: Promise<string | null> | null = null;

function refreshAccessTokenOnce(): Promise<string | null> {
	if (!pendingRefresh) {
		pendingRefresh = authService.forceRefreshAccessToken().finally(() => {
			pendingRefresh = null;
		});
	}
	return pendingRefresh;
}

// Response interceptor: Handle auth errors
apiClient.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const originalConfig = error.config as RetryableRequestConfig | undefined;

		if (error.response?.status !== 401 || !originalConfig || originalConfig._retry) {
			return Promise.reject(error);
		}

		originalConfig._retry = true;

		const newToken = await refreshAccessTokenOnce();

		if (newToken) {
			originalConfig.headers = originalConfig.headers ?? {};
			(originalConfig.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
			try {
				return await apiClient.request(originalConfig);
			} catch (retryError) {
				const retryStatus = (retryError as AxiosError).response?.status;
				if (retryStatus !== 401) {
					return Promise.reject(retryError);
				}
				// fall through to re-login on repeated 401
			}
		}

		await authService.signInAgain();
		return Promise.reject(error);
	}
);

// Custom mutator for Orval v8 with axios client
export const apiInstance = async <T>(
	config: {
		url: string;
		method: string;
		params?: any;
		data?: any;
		headers?: Record<string, string>;
		signal?: AbortSignal;
	},
	options?: any
): Promise<T> => {
	const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
	const response = await apiClient.request<T>({
		...config,
		signal: config.signal || undefined,
		headers: {
			...(isFormData ? {} : { 'Content-Type': 'application/json' }),
			...(options?.headers || {}),
		},
	});

	return response.data;
};
